import { Table } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import dayjs from 'dayjs'
import { get } from 'lodash'
import { useTranslation, Trans } from 'react-i18next'
import IconDetail from '~/assets/images/detail.svg'
import Tooltip from '~/components/Tooltip'
import { useBreakpoint } from '~/hooks'
import { formatNumber, formatPercentage } from '~/utils/numbers'
import { StyledRoundPTooltip } from './styled'

const PhaseRecord = ({ data, records }) => {
  const { isMobile } = useBreakpoint()
  const { t } = useTranslation()

  const getTextColor = (round) => {
    return round === 0 ? '#B9BED3' : 'inherit'
  }

  const columns: ColumnProps<any>[] = [
    {
      title: t('round_no'),
      dataIndex: 'round',
      key: 'round',
      align: 'center',
      width: 60,
      render: (round) => (
        <span style={{ color: getTextColor(round) }}>{round}</span>
      ),
    },
    {
      title: t('reward_quantity'),
      dataIndex: 'lon',
      key: 'lon',
      render: (lon, record) => (
        <span style={{ color: getTextColor(record.round) }}>
          {Number(lon) === 0 ? '-' : lon + ' LON'}
        </span>
      ),
      align: 'right',
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        return (
          <>
            <span style={{ color: status.color }}>{status.text}</span>
            {record.round === 0 && (
              <Tooltip
                className="icon-exclamation"
                placement="topRight"
                arrowPointAtCenter
                title={
                  <Trans i18nKey="expired_explain">
                    经 Tokenlon 社区提案
                    <a
                      href="https://snapshot.org/#/tokenlon.eth/proposal/0x8170797ba1c854221fd0e3649bc6f8900a821d0d913006c622f667d739a72c5a"
                      target="_blank"
                    >
                      TIP39
                    </a>
                    决定，逾期未认领的创世挖矿奖励已转入社区财库。
                  </Trans>
                }
              />
            )}
          </>
        )
      },
      align: 'right',
    },
  ]

  if (!isMobile) {
    columns.splice(
      2,
      0,
      {
        title: t('trade_power'),
        dataIndex: 'tradePower',
        key: 'tradePower',
        align: 'right',
        render: (tradePower, record) => (
          <span style={{ color: getTextColor(record.round) }}>
            {tradePower}
          </span>
        ),
      },
      {
        title: t('invitation_power_trade'),
        dataIndex: 'referralPower',
        key: 'referralPower',
        align: 'right',
        render: (referralPower, record) => (
          <span style={{ color: getTextColor(record.round) }}>
            {referralPower}
          </span>
        ),
      },
      {
        title: t('distributed_time'),
        dataIndex: 'distributedTime',
        key: 'distributedTime',
        align: 'right',
        render: (distributedTime, record) => (
          <span style={{ color: getTextColor(record.round) }}>
            {distributedTime}
          </span>
        ),
      },
      {
        title: t('detail'),
        dataIndex: 'detail',
        key: 'detail',
        align: 'right',
        width: 150,
        render: (detail, col) => {
          const phaseStart = get(
            records,
            ['pools', detail?.phase, 'startedAt'],
            '',
          )
          const phaseEnd = get(
            records,
            ['pools', detail?.phase, 'closedAt'],
            '',
          )
          const phase =
            phaseStart && phaseEnd
              ? dayjs(phaseStart * 1000).format('YYYY-MM-DD') +
                ' ~ ' +
                dayjs(phaseEnd * 1000).format('YYYY-MM-DD')
              : '--'

          return (
            <Tooltip
              overlayClassName="auto-width"
              color="#262c4b"
              trigger="click"
              title={
                <StyledRoundPTooltip>
                  <div className="popover-card" style={{ width: 575 }}>
                    <div className="card-header">
                      <dt>{`${t('number_of_reward')} (${
                        detail?.phase === 0
                          ? t('genesis_mining')
                          : `${t('round_no_1')}${String(detail?.phase)}${t(
                              'round_no_2',
                            )}`
                      })`}</dt>
                      <dd>{`${formatNumber(detail?.lon, 2)} LON`}</dd>
                      <div className="card-datetime">
                        <dt>{phase}</dt>
                        {detail?.grantTime && (
                          <dt>
                            {`${t('distributed_time')} ${dayjs(
                              detail?.grantTime * 1000,
                            ).format('YYYY-MM-DD')}`}
                          </dt>
                        )}
                      </div>
                    </div>
                    <div className="card-body">
                      <div>
                        <dt>{t('trade_reward')}</dt>
                        <dd className="highlight">{`${formatNumber(
                          detail?.tradeStats.lon,
                          2,
                        )} LON`}</dd>
                        <dt className="line2">{t('trade_amount')}</dt>
                        <dd>
                          {`$${formatNumber(
                            detail?.tradeStats.myTradeVolume,
                            2,
                          )}`}
                        </dd>
                      </div>
                      <div>
                        <dt>{t('computing_power_reward')}</dt>
                        <dd className="highlight">
                          {formatNumber(detail?.tradeStats.myTradePower, 2)}
                        </dd>
                        <dt className="line2">{t('total_power')}</dt>
                        <dd>
                          {formatNumber(detail?.tradeStats.tradePower, 2)}
                        </dd>
                      </div>
                      <div>
                        <dt>{t('weekly_power_rate')}</dt>
                        <dd className="highlight">
                          {formatPercentage(
                            detail?.tradeStats.tradePower === '0'
                              ? 0
                              : detail?.tradeStats.myTradePower /
                                  detail?.tradeStats.tradePower,
                            2,
                          )}
                        </dd>
                      </div>
                    </div>
                    <div className="card-body">
                      <div>
                        <dt>{t('referral_reward')}</dt>
                        <dd className="highlight">{`${formatNumber(
                          detail?.referralStats.lon,
                          2,
                        )} LON`}</dd>
                        <dt className="line2">{t('activated_address')}</dt>
                        <dd>{detail?.referralStats.activateInvitations}</dd>
                      </div>
                      <div>
                        <dt>{t('computing_invite_power_reward')}</dt>
                        <dd className="highlight">
                          {formatNumber(
                            detail?.referralStats.myReferralPower,
                            2,
                          )}
                        </dd>
                        <dt className="line2">{t('total_power')}</dt>
                        <dd>
                          {formatNumber(detail?.referralStats.referralPower, 2)}
                        </dd>
                      </div>
                      <div>
                        <dt>{t('weekly_power_rate')}</dt>
                        <dd className="highlight">
                          {formatPercentage(
                            detail?.referralStats.referralPower === '0'
                              ? 0
                              : detail?.referralStats.myReferralPower /
                                  detail?.referralStats.referralPower,
                            2,
                          )}
                        </dd>
                      </div>
                    </div>
                  </div>
                </StyledRoundPTooltip>
              }
            >
              <img src={IconDetail} alt="detail" />
            </Tooltip>
          )
        },
      },
    )
  }

  const _data = data?.map((item) => {
    const status = { text: '', color: '#B9BED3' }
    const { currentPhase } = records || {}
    const tradePower = Number(item?.tradeStats?.myTradePower)
    const referralPower = Number(item?.referralStats?.myReferralPower)
    const phase = item.phase
    const formatEndAt = item?.endAt * 1000
    if (formatEndAt > Date.now()) {
      status.text = t('waiting_for_count')
    }
    if (formatEndAt <= Date.now() && !item.audited && !item.settled) {
      status.text = t('in_calc')
    }
    if (item.audited && item.settled && !item.issued) {
      status.text = t('waiting_for_claiming')
      status.color = '#5F55A9'
    }
    if (item.audited && !item.settled) {
      status.text = t('settled_not_issued')
    }
    if (item.audited && item.settled && item.issued) {
      status.text = t('status_claimed')
    }
    if (tradePower === 0 && referralPower === 0 && phase < currentPhase) {
      status.text = '-'
    }
    if (phase === 0) {
      status.text = t('status_expired')
    }
    return {
      round: phase,
      lon: formatNumber(item.lon, 2),
      tradePower: formatNumber(tradePower, 2),
      referralPower: formatNumber(referralPower, 0),
      distributedTime: dayjs(item.grantTime * 1000).format('YYYY-MM-DD'),
      status,
      detail: item,
    }
  })

  return (
    <Table
      className="trade-awards-table"
      columns={columns}
      dataSource={_data}
      pagination={false}
      rowKey="round"
    />
  )
}

export default PhaseRecord
